import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { SavedUser } from '../../../../ngrx/user/user.interfaces';
import { selectSavedUser } from '../../../../ngrx/user/user.selectors';
import { loginStart, setLoginIsLoadingToFalse } from '../../../../ngrx/auth/auth.actions';
import { clearSavedUser } from '../../../../ngrx/user/user.actions';
import { selectLoginIsLoading } from '../../../../ngrx/auth/auth.selectors';
import { FormUtilityService } from 'src/app/modules/core/services/form-utility.service';
import { BeyCountrySelectorOptionInterface } from 'src/assets/interfaces/bey-country-selector.interface';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { phoneNumberValidator } from 'src/assets/utility/form.validators';
import { HttpClient } from '@angular/common/http';
import { IntercomService } from '../../../core/services/intercom.service';
import { BeyButtonComponent } from '../../../shared/components/bey-button/bey-button.component';
import { BeyInputPinComponent } from '../../../shared/components/bey-input-pin/bey-input-pin.component';
import { BeyCountryPhoneNumberInputComponent } from '../../../shared/components/bey-country-phone-number-input/bey-country-phone-number-input.component';
import { AutofocusDirective } from '../../../shared/directives/auto-focus.directive';
import { NgClass, NgIf, AsyncPipe, TitleCasePipe, NgTemplateOutlet } from '@angular/common';
import { GetElementIdDirective } from '../../../shared/directives/get-element-id.directive';
import { BeyExclamationIconComponent } from '../../../shared/components/icons/bey-exclamation-icon/bey-exclamation-icon.component';
import { CONST_NOW_ONAFRIQ_ALERT_CLOSED_KEY, ENV_NAMES, MERCHANT_COUNTRIES } from 'src/assets/const';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'auth-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  standalone: true,
  imports: [
    NgClass,
    ReactiveFormsModule,
    AutofocusDirective,
    NgIf,
    BeyCountryPhoneNumberInputComponent,
    BeyInputPinComponent,
    BeyButtonComponent,
    RouterLink,
    AsyncPipe,
    TitleCasePipe,
    GetElementIdDirective,
    BeyExclamationIconComponent,
    NgTemplateOutlet,
  ],
})
export class LoginComponent implements OnInit, OnDestroy {
  loginIsLoading$: Observable<boolean>;
  savedUser$: Observable<SavedUser>;
  savedUserSubscription: Subscription;
  form: UntypedFormGroup;
  countries: Array<BeyCountrySelectorOptionInterface> = [];
  showNowOnafriqAlert: boolean;

  // TODO: remove when Uganda is not the only supported country in production #ugandaOnly
  ugandaOnlyError: boolean = false;

  constructor(
    public router: Router,
    private fb: UntypedFormBuilder,
    private store$: Store,
    public formUtility: FormUtilityService,
    private http: HttpClient,
    private intercomUtil: IntercomService
  ) {
    this.form = new UntypedFormGroup({
      phone: new UntypedFormControl('', [Validators.required, phoneNumberValidator(true)]),
      PIN: new UntypedFormControl('', [
        Validators.required,
        Validators.minLength(4),
        Validators.maxLength(4),
        Validators.pattern(/^[0-9]*$/),
      ]),
    });

    if (environment.name !== ENV_NAMES.PROD) {
      this.countries.push(
        {
          label: 'Test Code',
          value: '+800',
        },
        // this is temp until we launch DRC officially
        {
          label: 'Democratic Republic of Congo',
          value: '+243',
          isoCode: 'cd',
        }
      );
    }

    this.countries = this.countries.concat(MERCHANT_COUNTRIES);
    // TODO: remove if check when Uganda is not the only supported country in production #ugandaOnly
    if (this.countries.length === 1) {
      this.form.get('phone').setValue(MERCHANT_COUNTRIES[0].value);
    }
  }

  ngOnInit(): void {
    this.loginIsLoading$ = this.store$.select(selectLoginIsLoading);
    this.savedUser$ = this.store$.select(selectSavedUser);

    this.loginIsLoading$
      .subscribe((isLoading) => {
        if (isLoading) {
          // if the isLoading variable equals true when we initiate the page then we reset it to false
          this.store$.dispatch(setLoginIsLoadingToFalse());
        }
      })
      .unsubscribe();

    this.savedUserSubscription = this.savedUser$.subscribe((savedUser) => {
      if (savedUser) {
        this.form.get('phone').clearValidators();
        this.form.get('phone').clearAsyncValidators();
      } else {
        this.form.get('phone').setValidators([Validators.required, phoneNumberValidator(true)]);
      }
      this.form.updateValueAndValidity();
    });

    const nowOnafriqAlertClosed = JSON.parse(localStorage.getItem(CONST_NOW_ONAFRIQ_ALERT_CLOSED_KEY));
    this.showNowOnafriqAlert = !nowOnafriqAlertClosed;
  }

  setValue(key: string, value: number) {
    this[key] = value;
  }

  changePhoneNumberClickHandler(): void {
    // clear intercom cookies of the old user
    this.intercomUtil.shutdown();
    this.intercomUtil.restart();
    this.store$.dispatch(clearSavedUser());
  }

  onSubmit(form: UntypedFormGroup) {
    if (form.valid) {
      let savedUser: SavedUser;
      this.savedUser$.subscribe((user) => (savedUser = user)).unsubscribe();

      const { phone, PIN } = form.value;

      this.store$.dispatch(
        loginStart({
          payload: {
            phone: savedUser ? savedUser.phone : phone,
            pin: PIN,
            known: !!savedUser,
          },
        })
      );
    } else {
      this.formUtility.validateAllFormFields(form);
    }
  }

  onXClick() {
    localStorage.setItem(CONST_NOW_ONAFRIQ_ALERT_CLOSED_KEY, 'true');
    this.showNowOnafriqAlert = false;
  }

  ngOnDestroy(): void {
    this.savedUserSubscription.unsubscribe();
  }
}
